import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHabitStore, useSettingsStore } from '@/store'
import { getCurrentCalendarDate, formatDateLocalized, formatDualDates, getCurrentJalaliDate, englishToFarsiNumber } from '@/lib/calendar'
import { Button, Input, Card, EmptyState } from '@/components/ui'
import { JalaliDatePicker } from '@/components/ui/JalaliDatePicker'

function formatCurrentDate(calendar: 'jalali' | 'gregorian', lang: 'fa' | 'en'): string {
  return formatDateLocalized(new Date(), calendar, lang)
}

interface HabitStats {
  completed: number
  pending: number
  streak: number
  progress: number
}

function computeStreak(completions: Array<{relativeDay: number, completionDate: string}>, totalDays: number): number {
  if (!Array.isArray(completions) || completions.length === 0) return 0
  
  // sort by relative day descending
  const sorted = [...completions].sort((a, b) => b.relativeDay - a.relativeDay)
  
  let count = 0
  for (let i = totalDays; i >= 1; i--) {
    const hasCompletion = sorted.some(c => c.relativeDay === i)
    if (hasCompletion) count++
    else break
  }
  return count
}

function getHabitStats(completions: Array<{relativeDay: number, completionDate: string}>, totalDays: number): HabitStats {
  const completed = completions.length
  const pending = Math.max(0, totalDays - completed)
  const progress = totalDays > 0 ? Math.round((completed / totalDays) * 100) : 0
  const streak = computeStreak(completions, totalDays)
  return { completed, pending, streak, progress }
}

export default function HabitTracker(): JSX.Element {
  const { t, i18n } = useTranslation()
  const { habits, addHabit, toggleDay, deleteHabit } = useHabitStore()
  const { calendar } = useSettingsStore()

  const [quickTitle, setQuickTitle] = useState('')
  const [goal, setGoal] = useState('30')
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    return d.toISOString().slice(0, 10)
  })
  const [endDate, setEndDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 29)
    return d.toISOString().slice(0, 10)
  })
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed'>('all')

  // عادات ہمیشہ با تاریخ جلالی ذخیره می‌شوند - برای فیلتر کردن این ماه (جلالی)
  const currentJalali = getCurrentJalaliDate()
  // برای نمایش تاریخ فعلی بر اساس انتخاب کاربر
  const displayDate = getCurrentCalendarDate(calendar)
  
  const monthHabits = Array.isArray(habits) ? habits.filter((h) => h.year === currentJalali.year && h.month === currentJalali.month) : []
  const todayIndex = currentJalali.day - 1

  const handleQuickAdd = useCallback((e?: React.FormEvent) => {
    e?.preventDefault()
    if (!quickTitle.trim()) return

    try {
      const goalDays = Math.min(Math.max(parseInt(goal) || 30, 1), 365)

      // parse and validate provided start/end dates
      let s = new Date(startDate)
      let en = new Date(endDate)
      if (isNaN(s.getTime())) s = new Date()
      if (isNaN(en.getTime())) { en = new Date(s); en.setDate(s.getDate() + goalDays - 1) }
      if (en < s) { const tmp = s; s = en; en = tmp }

      const diffDays = Math.ceil((en.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1
      const finalDays = Math.max(goalDays, diffDays)

      addHabit({
        title: quickTitle.trim(),
        monthlyGoal: goalDays,
        year: currentJalali.year,
        month: currentJalali.month,
        startDate: s.toISOString(),
        endDate: en.toISOString(),
      })

      setQuickTitle('')
      setGoal('30')
      // reset dates
      const nd = new Date()
      setStartDate(nd.toISOString().slice(0,10))
      nd.setDate(nd.getDate() + 29)
      setEndDate(nd.toISOString().slice(0,10))
    } catch (err) {
      console.error('Failed to add habit:', err)
    }
  }, [quickTitle, goal, addHabit, currentJalali, startDate, endDate])

  const filteredHabits = useMemo(() => {
    if (filterStatus === 'all') return monthHabits
    const totalDaysCache = new Map<string, number>()
    monthHabits.forEach(h => {
      const totalDays = Math.ceil((new Date(h.endDate).getTime() - new Date(h.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
      totalDaysCache.set(h.id, totalDays)
    })
    
    const today = monthHabits.map(h => getHabitStats(h.completions, totalDaysCache.get(h.id) || 30))
    if (filterStatus === 'active') {
      return monthHabits.filter((_, i) => !today[i].streak || today[i].streak < today[i].completed)
    }
    return monthHabits.filter((_, i) => today[i].progress === 100)
  }, [monthHabits, filterStatus])

  const stats = useMemo(() => {
    const totalHabits = monthHabits.length
    const completedToday = monthHabits.filter((h) => {
      const today = new Date().toISOString().slice(0, 10)
      const completions = Array.isArray(h.completions) ? h.completions : []
      return completions.some(c => c.completionDate.slice(0, 10) === today)
    }).length
    let maxStreak = 0
    monthHabits.forEach((h) => {
      const totalDays = Math.ceil((new Date(h.endDate).getTime() - new Date(h.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
      const s = computeStreak(h.completions, totalDays)
      if (s > maxStreak) maxStreak = s
    })
    const avgProgress = monthHabits.length > 0 ? Math.round(monthHabits.reduce((sum, h) => {
      const totalDays = Math.ceil((new Date(h.endDate).getTime() - new Date(h.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
      return sum + getHabitStats(h.completions, totalDays).progress
    }, 0) / monthHabits.length) : 0
    return { totalHabits, completedToday, maxStreak, avgProgress }
  }, [monthHabits])

  return (
    <div className="h-full flex flex-col fade-in bg-gray-100 dark:bg-slate-900">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800">
        <h1 className="text-4xl font-bold text-gray-800 dark:text-white">
          ✅ {t('sidebar.habit')}
        </h1>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scrollbar-custom px-6 py-6 bg-gray-100 dark:bg-slate-900 flex flex-col">

        {/* Stats Grid */}
        <div className="flex-shrink-0 grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="!p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.totalHabits) : stats.totalHabits}</div>
              <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">{t('habit.allHabits')}</div>
            </div>
          </Card>
          <Card className="!p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.completedToday) : stats.completedToday}</div>
              <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">{t('habit.today')}</div>
            </div>
          </Card>
          <Card className="!p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">🔥 {i18n.language === 'fa' ? englishToFarsiNumber(stats.maxStreak) : stats.maxStreak}</div>
              <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">{t('habit.longestStreak')}</div>
            </div>
          </Card>
          <Card className="!p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.avgProgress) : stats.avgProgress}%</div>
              <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">{t('habit.best')}</div>
            </div>
          </Card>
        </div>

        {/* Quick Add Form */}
        <form onSubmit={handleQuickAdd} className="flex-shrink-0 mb-6">
          <Card className="!p-5 border-2 border-dashed border-gray-200 dark:border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-8 gap-2 items-center">
              <Input 
                value={quickTitle} 
                onChange={(e) => setQuickTitle(e.target.value)} 
                placeholder={t('habit.quickAdd')}
                className="md:col-span-3"
              />
              <Input 
                type="number" 
                value={goal} 
                onChange={(e) => {
                  setGoal(e.target.value)
                  // update endDate default when goal changes
                  const g = parseInt(e.target.value) || 30
                  const s = new Date(startDate)
                  s.setDate(s.getDate() + g - 1)
                  setEndDate(s.toISOString().slice(0,10))
                }} 
                min={1} 
                max={365}
                placeholder={t('habit.monthlyGoal')}
              />
              <div className="md:col-span-2 flex gap-2">
                {calendar === 'jalali' ? (
                  <>
                    <JalaliDatePicker
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                    />
                    <JalaliDatePicker
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                    />
                  </>
                ) : (
                  <>
                    <input 
                      type="date" 
                      value={startDate} 
                      onChange={(e) => setStartDate(e.target.value)} 
                      className="w-full rounded px-2 py-1 bg-transparent border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white" 
                    />
                    <input 
                      type="date" 
                      value={endDate} 
                      onChange={(e) => setEndDate(e.target.value)} 
                      className="w-full rounded px-2 py-1 bg-transparent border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white" 
                    />
                  </>
                )}
              </div>
              <Button type="submit" variant="primary" className="md:col-span-1">
                ➕ {t('common.add')}
            </Button>
          </div>
        </Card>
      </form>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['all', 'active', 'completed'] as const).map((status) => (
          <Button
            key={status}
            variant={filterStatus === status ? 'primary' : 'ghost'}
            onClick={() => setFilterStatus(status)}
            size="sm"
          >
            {t(`habit.${status}`)}
          </Button>
        ))}
      </div>

      {/* Habits List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredHabits.length === 0 ? (
          <Card>
            <EmptyState 
              icon="inbox" 
              title={t('habit.noHabits')} 
              description={t('habit.addNew')} 
            />
          </Card>
        ) : (
          filteredHabits.map((habit) => {
            const startDate = new Date(habit.startDate)
            const endDate = new Date(habit.endDate)
            const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
            const completions = Array.isArray(habit.completions) ? habit.completions : []
            const habitStats = getHabitStats(completions, totalDays)
            
            const today = new Date().toISOString().slice(0, 10)
            const isCompletedToday = completions.some(c => c.completionDate.slice(0, 10) === today)
            const isGoalReached = habitStats.completed >= habit.monthlyGoal
            
            return (
              <Card 
                key={habit.id} 
                className="!p-5 hover:shadow-lg transition-shadow duration-200 border-l-4"
                style={{
                  borderColor: isGoalReached ? '#10b981' : isCompletedToday ? '#3b82f6' : '#e5e7eb',
                }}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  {/* Left Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {habit.title}
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {t('habit.monthlyGoal')}: {i18n.language === 'fa' ? englishToFarsiNumber(habit.monthlyGoal) : habit.monthlyGoal} {t('habit.days')} / {i18n.language === 'fa' ? englishToFarsiNumber(totalDays) : totalDays} {t('habit.days')}
                        </p>
                        {habit.startDate && habit.endDate && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatDateLocalized(habit.startDate, calendar, i18n.language === 'fa' ? 'fa' : 'en')}
                            {' '}—{' '}
                            {formatDateLocalized(habit.endDate, calendar, i18n.language === 'fa' ? 'fa' : 'en')}
                          </p>
                        )}
                      </div>
                      <button 
                        onClick={() => deleteHabit(habit.id)} 
                        className="text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-950 p-2 rounded transition-colors"
                        title={t('common.delete')}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Stats Row */}
                    <div className="flex gap-4 text-sm mb-3">
                      <div className="text-gray-700 dark:text-gray-300">
                        {habitStats.completed}/{totalDays} {t('habit.completedDays')}
                      </div>
                      <div className={`font-bold ${habitStats.streak > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        🔥 {habitStats.streak} {t('habit.days')}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-700 dark:text-gray-300">{t('habit.progress')}</span>
                        <span className={`text-sm font-bold ${habitStats.progress === 100 ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {i18n.language === 'fa' ? englishToFarsiNumber(habitStats.progress) : habitStats.progress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-400 via-green-400 to-teal-500" 
                          style={{ width: `${habitStats.progress}%` }} 
                        />
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        size="sm" 
                        variant={isCompletedToday ? 'primary' : 'outline'}
                        onClick={() => toggleDay(habit.id, today)}
                        className={isCompletedToday ? 'bg-green-600 hover:bg-green-700' : ''}
                      >
                        {isCompletedToday ? '✓ ' + t('habit.completed') : '○ ' + t('habit.markDoneToday')}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          const newTitle = prompt(t('habit.renamePrompt'), habit.title)
                          if (newTitle && newTitle.trim()) {
                            useHabitStore.getState().updateHabit(habit.id, { title: newTitle.trim() })
                          }
                        }}
                      >
                        ✎ {t('common.edit')}
                      </Button>
                    </div>
                  </div>

                  {/* Day Grid - Desktop */}
                  <div className="hidden md:block">
                    <div className="text-xs text-gray-400 mb-2 text-center">{t('habit.currentMonth')}</div>
                    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.ceil(totalDays / 5)}, 1fr)` }}>
                      {Array.from({ length: totalDays }, (_, i) => {
                        const relativeDay = i + 1
                        const dayDate = new Date(startDate)
                        dayDate.setDate(dayDate.getDate() + i)
                        const dateStr = dayDate.toISOString().slice(0, 10)
                        const isCompleted = completions.some(c => c.completionDate.slice(0, 10) === dateStr)
                        const isTodayHere = dateStr === today
                        
                        return (
                          <button 
                            key={i} 
                            onClick={() => toggleDay(habit.id, dateStr)}
                            className={`
                              w-6 h-6 rounded text-xs flex items-center justify-center font-semibold
                              transition-all duration-200 hover:scale-110
                              ${isCompleted
                                ? 'bg-green-500 text-white shadow-md' 
                                : isTodayHere
                                  ? 'bg-blue-900 text-blue-300 border-2 border-blue-400'
                                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                              }
                            `}
                            title={`${i18n.language === 'fa' ? 'روز' : 'Day'} ${relativeDay} - ${dateStr}`}
                          >
                            {relativeDay}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            )
          })
        )}
      </div>
      </div>
    </div>
  )
}
